import { Component, HostBinding, Input } from '@angular/core';

/**
 * Named column layouts for app-form-row. Screens pick a preset via
 * `layout="…"` instead of repeating raw grid classes, so a ratio change for a
 * whole screen family is a one-line edit here.
 *
 * Only add a preset when a pair is genuinely reused across screens; one-off
 * layouts keep using the labelCol/inputCol escape hatch.
 */
export type FormRowLayout =
  /** 3/9 admin default, stacks below lg. */
  | 'default'
  /** Payment-methods tab (incl. provider forms). */
  | 'payment'
  /** Settings tab (settings, default-catalog, branding, store-form media). */
  | 'settings'
  /** Integrations tab. */
  | 'integration'
  /** Integrations rows projecting their own columns (`extra-col`). */
  | 'integration-columns'
  /** Marketing tab (loyalty, promotions, vouchers, tracking, checkout-recs). */
  | 'marketing'
  /** Marketing-family toggle rows (voucher Active): toggle stays beside its label at every width. */
  | 'marketing-toggle'
  /** Marketing rows projecting their own columns. */
  | 'marketing-columns'
  /** Catalog tab (offers, variants, categories, sales points, printers). */
  | 'catalog'
  /** Catalog rows projecting their own columns. */
  | 'catalog-columns'
  /** Catalog full-width stacked label-above-input rows. */
  | 'catalog-stacked'
  /** Catalog stock-check row: two label+control pairs. */
  | 'catalog-stock'
  /** Schedule tab. */
  | 'schedule'
  /** Schedule rows projecting their own columns. */
  | 'schedule-columns'
  /** Schedules matrix-aligned control row (label col-3 from md up). */
  | 'schedule-matrix'
  /** Address & delivery tab. */
  | 'delivery'
  /** Inline toggle rows (reservations / delivery / pickup). */
  | 'toggle'
  /** Generic label+toggle pair sharing the row at EVERY width (product decision). */
  | 'toggle-inline'
  /** Table-ordering toggle rows (wider 6-col input). */
  | 'table-ordering'
  /** Store-zone screens. */
  | 'zone'
  /** Store-domain screens. */
  | 'domain'
  /** Ordering-rules screens. */
  | 'ordering'
  /** Reservations tab. */
  | 'reservation'
  /** Reservations rows projecting their own columns. */
  | 'reservation-columns'
  /** Reservations dialog rows. */
  | 'reservation-modal'
  /** Billing-details screens. */
  | 'billing'
  /** Billing postcode/region/city row: three inline pairs. */
  | 'billing-multi'
  /** Store-location address blocks. */
  | 'address'
  /** Address postcode/region/city row: inline pairs. */
  | 'address-multi'
  /** Events tab. */
  | 'event'
  /** Event banner+map row: four flex 3-col cells. */
  | 'event-media'
  /** Floor-plans screens. */
  | 'floor-plan'
  /** Create-customer screens. */
  | 'customer-form'
  /** Refund dialog. */
  | 'refund-dialog'
  /** Store-relation screens. */
  | 'relation'
  /** Restored width variant (original geometry). */
  | 'payment-compact'
  /** Restored width variant (original geometry). */
  | 'payment-medium'
  /** Restored width variant (original geometry). */
  | 'payment-wide'
  /** Restored width variant (original geometry). */
  | 'payment-inline'
  /** Restored width variant (original geometry). */
  | 'payment-header'
  /** Restored width variant (original geometry). */
  | 'payment-toggle'
  /** Restored width variant (original geometry). */
  | 'provider'
  /** Restored width variant (original geometry). */
  | 'provider-compact'
  /** Restored width variant (original geometry). */
  | 'settings-header'
  /** Restored width variant (original geometry). */
  | 'settings-header-compact'
  /** Integration Active-toggle rows: toggle beside its label at every width. */
  | 'integration-toggle'
  /** Restored width variant (original geometry). */
  | 'integration-compact'
  /** Restored width variant (original geometry). */
  | 'integration-stack'
  /** Restored width variant (original geometry). */
  | 'integration-wide'
  /** Restored width variant (original geometry). */
  | 'integration-columns-md'
  /** Restored width variant (original geometry). */
  | 'tracking-header'
  /** Restored width variant (original geometry). */
  | 'voucher-toggle'
  /** Restored width variant (original geometry). */
  | 'voucher-search'
  /** Restored width variant (original geometry). */
  | 'voucher-code'
  /** Restored width variant (original geometry). */
  | 'loyalty-discount'
  /** Restored width variant (original geometry). */
  | 'zone-unit'
  /** Restored width variant (original geometry). */
  | 'domain-wide'
  /** Restored width variant (original geometry). */
  | 'toggle-wide'
  /** Restored width variant (original geometry). */
  | 'billing-sub'
  /** Restored width variant (original geometry). */
  | 'address-sub'
  /** Restored width variant (original geometry). */
  | 'address-phone'
  /** Restored width variant (original geometry). */
  | 'event-inline'
  /** Restored width variant (original geometry). */
  | 'schedule-inline'
  /** Restored width variant (original geometry). */
  | 'schedule-name'
  /** Restored width variant (original geometry). */
  | 'schedule-slot'
  /** Restored width variant (original geometry). */
  | 'reservation-toggle'
  /** Restored width variant (original geometry). */
  | 'reservation-strategy'
  /** Restored width variant (original geometry). */
  | 'reservation-amount'
  /** Restored width variant (original geometry). */
  | 'reservation-spacer'
  /** Schedule select rows: 4-col select, Add-schedule button projected as extra-col beside it. */
  | 'reservation-schedule'
  /** Inline 3-option radio rows (Hidden/Optional/Mandatory): 8-col input so options don't wrap while the row has free width. */
  | 'radio-row'
  /** Restored width variant (original geometry). */
  | 'catalog-narrow'
  /** Restored width variant (original geometry). */
  | 'catalog-row'
  /** Restored width variant (original geometry). */
  | 'category-name'
  /** Restored width variant (original geometry). */
  | 'offer-price'
  /** Restored width variant (original geometry). */
  | 'offer-sameday'
  /** Restored width variant (original geometry). */
  | 'offer-loyalty-calc'
  /** Restored width variant (original geometry). */
  | 'offer-image'
  /** Restored width variant (original geometry). */
  | 'sales-point-name'
  /** Restored width variant (original geometry). */
  | 'price-spacer'
  /** Restored width variant (original geometry). */
  | 'variant-spacer'
  /** Restored width variant (original geometry). */
  | 'spacer-default'
  /** Restored width variant (original geometry). */
  | 'floor-plan-image'
  /** Restored width variant (original geometry). */
  | 'delivery-compact'
  /** Branding tab: narrow 1-2 col label, radios directly beside it. */
  | 'branding'
  /** Password-auth prompt row (label only). */
  | 'auth-prompt'
  /** Public dashboard open-time modal. */
  | 'dashboard-social';

// ── ADMIN-WIDE COLUMN STANDARD (user decision 2026-08-18) ────────────────
// Every simple label + single-control row shares ONE desktop column split,
// regardless of screen or preset: label 4 cols, control the FULL remaining
// 8 cols at every desktop breakpoint (user correction 2026-08-18: controls
// span the remainder — no half-empty columns). Enforced at class-resolution
// time, so a developer picking ANY preset gets the standard from md up —
// presets contribute only their sub-md (phone) behavior and non-width
// utility classes. This ended per-screen drift like two dropdowns on one
// screen rendering 33% vs 50% wide.
export const STANDARD_DESKTOP_LABEL = 'col-md-4 col-lg-4 col-xl-4';
export const STANDARD_DESKTOP_INPUT = 'col-md-8 col-lg-8 col-xl-8';

/** Structural presets exempt from the standard (see isStandardExempt). */
const STANDARD_EXEMPT_LAYOUTS: FormRowLayout[] = [
  'catalog-stacked',
  'schedule-matrix',
  'schedule-inline',
  'schedule-columns',
  'schedule-slot',
  // Not structural itself, but column-locked to the structural matrix rows
  // beside it: the preset's ≥992px 25% label exists to share the delivery-mode
  // matrix's first-column edge. The standard's 33% label pushed these rows'
  // controls ~8% right of the matrix toggles below them (prod/dev report,
  // 2026-08-24, Schedules tab at 1430px).
  'schedule',
  'dashboard-social',
];

/**
 * Strip a preset's md-and-up col-* width tokens and append the standard
 * ones. Breakpointless col-N and col-sm-N tokens survive as the phone
 * behavior (Bootstrap's md+ media rules override them from md up), as do
 * non-width utilities (d-flex, pt-1, …).
 */
function standardizeDesktopCols(classes: string, standard: string): string {
  const kept = classes
    .split(/\s+/)
    .filter((c) => !/^col-(md|lg|xl|xxl)-(\d+|auto)$/.test(c));
  return [...kept, standard].join(' ').trim();
}

export const FORM_ROW_LAYOUTS: Record<
  FormRowLayout,
  { label: string; input: string; label2?: string; input2?: string }
> = {
  default: {
    label: 'col-xl-3 col-lg-3 col-md-12 col-sm-12',
    input: 'col-xl-9 col-lg-9 col-md-12 col-sm-12',
  },
  payment: {
    label: 'col-md-3',
    input: 'col-xl-9 col-lg-9 col-md-12 col-sm-12',
  },
  settings: {
    label: 'col-xl-4 col-lg-4 col-md-4 col-sm-12',
    input: 'col-xl-8 col-lg-8 col-md-8 col-sm-12',
  },
  integration: {
    label: 'col-xl-3 col-lg-3 col-md-3 col-sm-12',
    input: 'col-xl-9 col-lg-9 col-md-9 col-sm-12',
  },
  'integration-columns': {
    label: 'col-xl-3 col-lg-3 col-md-3 col-sm-12',
    input: 'none',
  },
  marketing: {
    label: 'col-xl-4 col-lg-4 col-md-3 col-sm-12',
    input: 'col-xl-8 col-lg-8 col-md-9 col-sm-12',
  },
  // Label + toggle share the row at EVERY width (same product decision as the
  // payment providers' Active rows): a toggle is one unit with its label.
  'marketing-toggle': {
    label: 'col-xl-4 col-lg-4 col-md-3 col-4',
    input: 'col-xl-8 col-lg-8 col-md-9 col-8',
  },
  'marketing-columns': {
    label: 'col-xl-4 col-lg-4 col-md-3 col-sm-12',
    input: 'none',
  },
  catalog: {
    label: 'col-xl-2 col-lg-3 col-md-12 col-sm-12',
    input: 'col-xl-10 col-lg-9 col-md-12 col-sm-12',
  },
  'catalog-columns': {
    label: 'col-xl-2 col-lg-3 col-md-12 col-sm-12',
    input: 'none',
  },
  'catalog-stacked': {
    label: 'col-md-12',
    input: 'col-md-12',
  },
  'catalog-stock': {
    label: 'col-xl-2 col-lg-3 col-md-12 col-sm-12 pt-1',
    input:
      'col-xl-3 col-lg-3 col-md-12 col-sm-12 d-flex flex-wrap align-items-center mb-2 mb-0',
    label2: 'col-xl-2 col-lg-2 col-md-12 col-sm-12 pt-1',
    input2: 'col-xl-5 col-lg-4 col-md-12 col-sm-12',
  },
  schedule: {
    // The delivery-mode matrix's 25% label column at EVERY width from md up,
    // so all rows on the Schedules tab share one label edge with the matrix
    // toggles (user 2026-08-24: the earlier 33% md band left the single-row
    // controls off the matrix column below 992 — alignment beats the wider
    // label; text-heavy "… - Ordering times" labels wrap in the 768–991 band
    // by design). Below 768px labels stack full-width.
    label: 'col-12 col-md-3',
    input: 'col-12 col-md-9',
  },
  'schedule-columns': {
    label: 'col-3',
    input: 'none',
  },
  'schedule-matrix': {
    // A single control spanning the Schedules matrix (label col-3 + 3×col-3):
    // the label keeps the matrix's 25% at EVERY width — like the matrix rows
    // themselves (§50c, develop parity), phones included; wrapping label text
    // is the intended look. Grid-mode options (col-md-4 of the col-9 input =
    // 25% of the row each) land exactly under the three mode columns from md
    // up and stack inside the control column below. The `schedule` preset
    // can't serve here — its 33% md label was chosen for the text-heavy
    // "Ordering times" rows and shifts everything a column off the matrix.
    label: 'col-3',
    input: 'col-9',
  },
  delivery: {
    label: 'col-md-4',
    input: 'col-md-6',
  },
  'toggle-inline': {
    label: 'col-xl-4 col-lg-4 col-md-4 col-4',
    input: 'col-xl-8 col-lg-8 col-md-8 col-8',
  },
  toggle: {
    label: 'col-xl-4 col-lg-4 col-md-4 col-sm-4 col-4',
    input: 'col-xl-4 col-lg-4 col-md-4 col-sm-6 col-6',
  },
  'table-ordering': {
    label: 'col-xl-4 col-lg-4 col-md-4 col-sm-4 col-4',
    input: 'col-xl-6 col-lg-4 col-md-4 col-sm-6 col-6',
  },
  zone: {
    label: 'col-xl-4 col-lg-4 col-md-12 col-sm-12',
    input: 'col-xl-8 col-lg-8 col-md-12 col-sm-12',
  },
  domain: {
    label: 'col-xl-4 col-lg-4 col-md-12 col-sm-12',
    input: 'col-xl-8 col-lg-8 col-md-12 col-sm-12',
  },
  ordering: {
    label: 'col-xl-2 col-lg-3 col-md-3 col-sm-12',
    input: 'col-xl-10 col-lg-9 col-md-9 col-sm-12',
  },
  reservation: {
    // col-sm-4/8: from 576px up the label and control share the row (toggles
    // sit beside their labels, inputs take the full remaining width) instead
    // of stacking with an empty right half.
    label: 'col-xl-4 col-lg-4 col-md-4 col-sm-4',
    // Full-width inputs on phones (col-12) like the rest of the admin; the
    // old col-6 left half-width selects/inputs at 375px.
    input: 'col-xl-4 col-lg-4 col-md-4 col-sm-8 col-12',
  },
  'reservation-columns': {
    // col-sm-4 col-4 keep the spacer at label width below md (develop parity):
    // without them it collapses to auto width and the extra-col shifts left.
    label: 'col-xl-4 col-lg-4 col-md-4 col-sm-4 col-4',
    input: 'none',
  },
  'reservation-modal': {
    label: 'col-4',
    input: 'col-8',
  },
  billing: {
    label: 'col-xl-2 col-lg-2 col-md-2 col-sm-2',
    input: 'col-xl-9 col-lg-9 col-md-9 col-sm-9',
  },
  'billing-multi': {
    label: 'col-xl-2 col-md-2 col-sm-2',
    input: 'col-xl-2 col-lg-12 mb-2',
    label2: 'col-xl-2 col-md-2 col-sm-2',
    input2: 'col-xl-2 col-lg-12 mb-2',
  },
  address: {
    label: 'col-xl-2 col-lg-3 col-md-12 col-sm-12',
    input: 'col-xl-10 col-lg-9 col-md-12 col-sm-12',
  },
  'address-multi': {
    label: 'col-xl-2 col-lg-12',
    input: 'input-group col-xl-2 col-lg-12 mb-2',
    label2: 'col-xl-2 col-lg-12 text-xl-right',
    input2: 'input-group col-xl-2 col-lg-12 mb-2',
  },
  event: {
    label: 'col-xl-3 col-lg-3 col-md-3 col-sm-12',
    input: 'col-xl-8 col-lg-8 col-md-8 col-sm-12',
  },
  'event-media': {
    label: 'col-xl-3 col-lg-3 col-md-3 col-sm-12 d-flex mb-2 align-items-top',
    input: 'col-xl-3 col-lg-3 col-md-3 col-sm-12 d-flex mb-2 align-items-top',
    label2: 'col-xl-3 col-lg-3 col-md-3 col-sm-12 d-flex mb-2 align-items-top',
    input2: 'col-xl-3 col-lg-3 col-md-3 col-sm-12 d-flex mb-2 align-items-top',
  },
  'floor-plan': {
    label: 'col-12 col-sm-3 col-lg-3 col-xl-2',
    input: 'col-12 col-sm-9 col-lg-9 col-xl-10',
  },
  'customer-form': {
    label: 'col-lg-4 col-md-4 col-sm-12',
    input: 'col-lg-6 col-md-6 col-sm-12',
  },
  'refund-dialog': {
    label: 'col-lg-4 col-md-4 col-sm-4',
    input: 'col-lg-8 col-md-8 col-sm-8',
  },
  relation: {
    label: 'col-xl-4 col-lg-4 col-md-4',
    input: 'col-md-8',
  },
  // Provider "Active" toggle rows. col-4/col-8 keep the toggle BESIDE its
  // label at phone widths too (product decision: the Active toggle sits in
  // front of its label in every view, unlike the stacked text rows above it).
  'payment-compact': {
    label: 'col-md-3 col-4',
    input: 'col-md-4 col-8',
  },
  'payment-medium': {
    label: 'col-md-3 col-4',
    input: 'col-md-5 col-8',
  },
  'payment-wide': {
    label: 'col-md-3 col-4',
    input: 'col-md-6 col-8',
  },
  'payment-inline': {
    label: 'col-xl-3 col-lg-3 col-md-4 col-sm-4 col-4',
    input: 'col-md-6',
  },
  'payment-header': {
    label: 'col-xl-4 col-lg-5',
    input: 'col-md-5',
  },
  'payment-toggle': {
    label: 'col-xl-4 col-lg-5',
    input:
      'd-flex flex-md-row flex-sm-column flex-column col-xl-4 col-lg-4 col-md-4 col-sm-6 col-6',
  },
  provider: {
    label: 'col-md-2',
    input: 'col-xl-9 col-lg-9 col-md-12 col-sm-12',
  },
  'provider-compact': {
    label: 'col-md-2',
    input: 'col-md-4',
  },
  'settings-header': {
    label: 'col-xl-4 col-lg-4 col-md-4',
    input: 'col-md-5',
  },
  'settings-header-compact': {
    label: 'col-xl-4 col-lg-4 col-md-4',
    input: 'col-md-4',
  },
  // Label + toggle share the row at EVERY width (same product decision as the
  // payment providers' Active rows).
  'integration-toggle': {
    label: 'col-xl-3 col-lg-3 col-md-3 col-4',
    input: 'col-xl-9 col-lg-9 col-md-9 col-8',
  },
  'integration-compact': {
    label: 'col-md-3',
    input: 'col-md-9',
  },
  'integration-stack': {
    label: 'col-xl-3 col-lg-3 col-md-12',
    input: 'col-xl-9 col-lg-9 col-md-12',
  },
  'integration-wide': {
    label: 'col-xl-3 col-lg-3 col-md-3 col-sm-12',
    input: 'col-xl-9 col-lg-9 col-md-12 col-sm-12',
  },
  'integration-columns-md': {
    label: 'col-md-3',
    input: 'none',
  },
  'tracking-header': {
    label: 'col-md-4',
    input: 'col-md-5',
  },
  'voucher-toggle': {
    // col-4/8 at xs: the toggle stays beside its label on phones too
    // (standing product decision for label+toggle pairs).
    label: 'col-md-4 col-sm-6 col-4',
    input: 'col-md-8 col-sm-6 col-8',
  },
  'voucher-search': {
    label: 'col-md-4 col-sm-6 col-12',
    input: 'col-md-8 col-sm-6 col-12',
  },
  'voucher-code': {
    label: 'col-xl-4 col-lg-4 col-md-3 col-sm-12',
    input: 'col-xl-5 col-lg-4 col-md-6 col-sm-12',
  },
  'loyalty-discount': {
    label: 'col-xl-4 col-lg-4 col-md-4 col-sm-12',
    input: 'col-xl-8 col-lg-8 col-md-8 col-sm-12',
  },
  'zone-unit': {
    label: 'col-xl-4 col-lg-4 col-md-12 col-sm-12',
    input: 'col-md-8',
  },
  'domain-wide': {
    label: 'col-md-4',
    input: 'col-md-8',
  },
  'toggle-wide': {
    label: 'col-xl-4 col-lg-4 col-md-4 col-sm-4 col-4',
    input: 'col-xl-6 col-lg-6 col-md-6 col-sm-6 col-6',
  },
  'billing-sub': {
    label: 'col-xl-2 col-md-2 col-sm-2',
    input: 'col-xl-10 col-lg-10 col-md-10 col-sm-10',
  },
  'address-sub': {
    label: 'col-xl-2 col-md-12 col-sm-12',
    input: 'col-xl-10 col-md-12 col-sm-12',
  },
  'address-phone': {
    label: 'col-xl-2 col-lg-3 col-md-12 col-sm-12',
    input: 'col-xl-10 col-md-12 col-sm-12',
  },
  'event-inline': {
    label: 'col-xl-3 col-lg-3 col-md-3 col-sm-12',
    input: 'col-md-5',
  },
  'schedule-inline': {
    label: 'col-3',
    input: 'col-9',
  },
  'schedule-name': {
    label: 'col-lg-3',
    input: 'col-lg-7',
  },
  'schedule-slot': {
    label: 'col-4',
    input: 'col-4',
  },
  'reservation-toggle': {
    label: 'col-xl-4 col-lg-4 col-md-4 col-sm-4',
    // md: the toggle+store-URL composite gets the full remaining width — a
    // 4-col input wrapped the URL mid-word beside an empty half-row.
    input: 'col-xl-5 col-lg-5 col-md-8 col-sm-8',
  },
  'reservation-strategy': {
    label: 'col-xl-4 col-lg-4 col-md-4 col-sm-4',
    input: 'col-xl-8 col-lg-8 col-md-8 col-sm-6',
  },
  'reservation-amount': {
    label: 'col-xl-4 col-lg-4 col-md-4',
    input: 'd-flex flex-column',
  },
  'reservation-spacer': {
    label: 'col-xl-4 col-lg-4 col-md-4',
    input: 'col-xl-8 col-lg-8 col-md-12 col-sm-12',
  },
  'radio-row': {
    // Same pattern as reservation-strategy: a col-4 input wrapped the third
    // option ("Mandatory") onto its own line at laptop widths even though the
    // row had free space — the radios get the remaining 8 cols instead.
    // col-4/col-8 at xs too (user decision 2026-08-13, develop parity): the
    // label keeps its column beside the stacked options — a wrapping label
    // ("Name on / doorbell") is correct there, not a defect.
    label: 'col-xl-4 col-lg-4 col-md-4 col-sm-4 col-4',
    input: 'col-xl-8 col-lg-8 col-md-8 col-sm-6 col-8',
  },
  'reservation-schedule': {
    // Two-column at every width from sm up (user decision 2026-08-12): label
    // left, select right — the Add-schedule button wraps under the select and
    // is column-aligned by the screen's offset rule. Below sm it stacks.
    label: 'col-xl-4 col-lg-4 col-md-4 col-sm-4',
    input: 'col-xl-4 col-lg-6 col-md-8 col-sm-8',
  },
  'catalog-narrow': {
    label: 'col-xl-3 col-lg-3 col-md-12 col-sm-12',
    input: 'col-xl-8 col-lg-9 col-md-12 col-sm-12',
  },
  'catalog-row': {
    label: 'col-xl-2 col-lg-3 col-md-12 col-sm-12',
    input: 'col-xl-9 col-lg-9 col-md-12 col-sm-12',
  },
  'category-name': {
    label: 'col-xl-2 col-lg-3 col-md-3 col-sm-12',
    input: 'col-xl-10 col-lg-9 col-md-9 col-sm-12',
  },
  'offer-price': {
    label: 'col-xl-3 col-lg-3 col-md-12 col-sm-12',
    input: 'col-xl-4 col-lg-9 col-md-12 col-sm-12',
  },
  'offer-sameday': {
    label: 'col-xl-3 col-lg-3 col-md-12 col-sm-12',
    input: 'col-xl-4 col-lg-6 col-md-12 col-sm-12',
  },
  'offer-loyalty-calc': {
    // Develop's 2+8+2: the 2-col factor input must fit on the same line
    // after the select (a col-xl-3 label made the row 13 cols and wrapped it
    // under the label).
    label: 'col-xl-2 col-lg-3 col-md-12 col-sm-12',
    input: 'col-xl-8 col-lg-7 col-md-12 col-sm-12',
  },
  'offer-image': {
    label: 'col-sm-2',
    input: 'col-sm-10',
  },
  'sales-point-name': {
    label: 'col-xl-2 col-lg-3 col-md-12 col-12',
    input: 'col-xl-10 col-lg-9 col-md-12 col-12',
  },
  'price-spacer': {
    label: 'col-xl-2 col-lg-3',
    input: 'col-xl-10 col-lg-9 col-md-12 col-sm-12',
  },
  'variant-spacer': {
    label: 'col-xl-2 col-lg-3',
    input: 'col-lg-8 col-md-12 col-sm-12',
  },
  'spacer-default': {
    label: 'col-xl-2 col-lg-3',
    input: 'col-xl-9 col-lg-9 col-md-12 col-sm-12',
  },
  'floor-plan-image': {
    label: 'col-4 col-sm-3 col-lg-3 col-xl-2',
    input: 'col-8 col-sm-9 col-lg-9 col-xl-10',
  },
  'delivery-compact': {
    label: 'col-md-4',
    input: 'col-md-4',
  },
  branding: {
    label: 'col-xl-1 col-lg-2 col-md-2',
    input: 'col-md-5',
  },
  'auth-prompt': {
    label: 'col-lg-12 col-md-12 col-sm-12',
    input: 'none',
  },
  'dashboard-social': {
    label: 'col-sm-6 col-md-6 col-lg-5',
    input: 'col-sm-6 col-md-6 col-lg-5',
  },
};

/**
 * Reusable form-row layout for admin screens. Owns the repeated
 * `.row.form-group` + label-column + input-column structure so screens stop
 * hand-writing the same grid/label boilerplate around every field.
 *
 * The actual control(s) — a raw input, an app-select, an app-toggle, an
 * invalid-feedback block, anything — are projected via <ng-content> into the
 * input column, so this works with any control type and changes nothing
 * visually.
 *
 * Example:
 *   <app-form-row label="admin.global.name" labelFor="offerName" [required]="true">
 *     <input class="form-control" formControlName="name" id="offerName" />
 *     <div class="invalid-feedback" *ngIf="...">...</div>
 *   </app-form-row>
 *
 * Slots:
 *   (default)        → the input column (control + validation)
 *   [label-extra]    → extra content inside the label (counter, custom icon…)
 */
@Component({
  selector: 'app-form-row',
  templateUrl: './form-row.component.html',
  styleUrls: ['./form-row.component.scss'],
})
export class FormRowComponent {
  /** Translation key for the label. Empty renders a spacer label (keeps grid alignment). */
  @Input() label = '';
  /** `for` attribute target — the projected control's id. */
  @Input() labelFor = '';
  /** Render the required asterisk on the label. */
  @Input() required = false;
  /** Teal help-icon (?) beside the label — forwarded to app-help-icon. */
  @Input() helpPageName = '';
  /**
   * Named column preset — the preferred way to size the row. Resolves both
   * columns from FORM_ROW_LAYOUTS so shared layouts are defined in one place.
   */
  @Input() layout: FormRowLayout = 'default';
  /**
   * Extra NON-GRID classes appended to the preset's label column (e.g.
   * `form-header`, `hideInSmallScreen`, `required-field`). Grid col-* classes
   * belong in the FORM_ROW_LAYOUTS preset, never here.
   */
  @Input() labelClass = '';
  /** Extra NON-GRID classes appended to the preset's input column (e.g. `input-group`, `priceContainer`). */
  @Input() inputClass = '';
  /** Row wrapper classes — applied to the host element, which IS the `.row`. */
  @Input() rowClass = 'row form-group';

  /**
   * Optional second label + control pair on the same row (e.g. the
   * "Stock Check … Stock Level …" row). The second control is projected via
   * the `second-control` attribute. Empty label2 renders nothing.
   */
  @Input() label2 = '';
  @Input() label2For = '';

  /**
   * The host element becomes the Bootstrap `.row` itself (no wrapper element),
   * so the rendered DOM is identical to the original hand-written
   * `<div class="row form-group">…</div>` and the grid is unaffected.
   */
  @HostBinding('class')
  get hostClasses(): string {
    return this.rowClass;
  }

  /**
   * A preset is exempt from the admin-wide column standard when it is
   * structural rather than a simple label+control pair: projected-column
   * rows (input 'none'), two-pair rows (label2), stacked labels, and the
   * Schedules matrix family whose columns align to a 4-column grid.
   */
  private get isStandardExempt(): boolean {
    const def = FORM_ROW_LAYOUTS[this.layout];
    return (
      STANDARD_EXEMPT_LAYOUTS.includes(this.layout) ||
      def.input === 'none' ||
      !!def.label2
    );
  }

  get labelColClasses(): string {
    const def = FORM_ROW_LAYOUTS[this.layout];
    const base = this.isStandardExempt
      ? def.label
      : standardizeDesktopCols(def.label, STANDARD_DESKTOP_LABEL);
    return this.labelClass ? `${base} ${this.labelClass}` : base;
  }

  get inputColClasses(): string {
    const def = FORM_ROW_LAYOUTS[this.layout];
    if (def.input === 'none') {
      return def.input;
    }
    const base = this.isStandardExempt
      ? def.input
      : standardizeDesktopCols(def.input, STANDARD_DESKTOP_INPUT);
    return this.inputClass ? `${base} ${this.inputClass}` : base;
  }

  get label2ColClasses(): string {
    return (
      FORM_ROW_LAYOUTS[this.layout].label2 ||
      'col-xl-2 col-lg-2 col-md-12 col-sm-12'
    );
  }

  get input2ColClasses(): string {
    return (
      FORM_ROW_LAYOUTS[this.layout].input2 ||
      'col-xl-5 col-lg-4 col-md-12 col-sm-12'
    );
  }
}
